import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchApplicationsForFinalReview } from '~/services/campaign';
import { formatDate, parseISO } from 'date-fns';
import { useAuth } from '~/context/AuthProvider';

type FinalReviewApplication = {
    id: number;
    campaign_id: number | null;
    patient_name: string;
    campaign_type: string;
    update_date: string | null;
};

const columnHelper = createColumnHelper<FinalReviewApplication>();

const columns = [
    columnHelper.accessor('patient_name', {
        header: () => 'Client',
        cell: (props) => {
            return (
                <Link
                    className='text-primary'
                    to={`/campaign-applications/${props.row.original.id}`}
                >
                    {props.renderValue()}
                </Link>
            );
        },
    }),
    columnHelper.accessor('campaign_type', {
        header: () => 'Medical Reason',
        cell: (info) => info.getValue(),
    }),
    columnHelper.accessor(
        (row) =>
            row.update_date
                ? formatDate(parseISO(row.update_date), 'MM/dd/yy')
                : '',
        {
            id: 'last_updated',
            header: () => 'Last Updated',
            cell: (props) => props.renderValue(),
        }
    ),
];

const ApplicationsForFinalReview = () => {
    const { person } = useAuth();
    const { data, isLoading } = useQuery({
        queryKey: ['applications-for-final-review'],
        queryFn: fetchApplicationsForFinalReview,
        staleTime: Infinity,
        gcTime: Infinity,
    });

    const applications: FinalReviewApplication[] = data?.results || [];

    const table = useReactTable({
        data: applications,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const viewAllUrl = person?.id
        ? `/campaign-applications?assignee=${person.id}&status=completed`
        : '/campaign-applications?status=completed';

    return (
        <section className='data-table-common h-full rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'>
            <div className='flex flex-col gap-3 p-4 pb-2 sm:flex-row sm:items-center sm:justify-between md:p-6 md:pb-2 xl:p-7.5 xl:pb-2'>
                <h2 className='text-xl font-semibold text-black dark:text-white'>
                    Applications For Final Review
                </h2>

                <Link
                    to={viewAllUrl}
                    className='flex items-center gap-2 text-primary'
                >
                    View All <ArrowRightSvg />
                </Link>
            </div>

            <div className='overflow-x-auto'>
                <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                    <thead className='border-separate px-4'>
                        {isLoading
                            ? Array.from({ length: 5 }).map((_, index) => (
                                  <tr key={index}>
                                      {columns.map((_column, colIndex) => (
                                          <td
                                              key={colIndex}
                                              className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                          >
                                              <div className='max-w-sm animate-pulse'>
                                                  <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      ))}
                                  </tr>
                              ))
                            : table.getHeaderGroups().map((headerGroup) => (
                                  <tr
                                      style={{ fontSize: '14px' }}
                                      key={headerGroup.id}
                                  >
                                      {headerGroup.headers.map((header) => (
                                          <th
                                              className='text-secondary'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                              key={header.id}
                                          >
                                              {header.isPlaceholder
                                                  ? null
                                                  : flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                          </th>
                                      ))}
                                  </tr>
                              ))}
                    </thead>
                    <tbody>
                        {!isLoading && applications.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className='px-4 py-8 text-center text-secondary'
                                >
                                    No applications pending final review
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    className='border-t border-stroke'
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={cell.id}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default ApplicationsForFinalReview;
